import React, { useEffect, useState } from "react";
import { useHistory } from 'react-router-dom'
import { TShipmentManagement } from "./state_management/types";
import { connect } from "react-redux";
import { ShipmentState, Dispatcher, RootState, AppState } from "AppTypes";
import { shipmentActions } from "./state_management/actions";
import { appActions } from "@app/state_management/actions";
import LoadingSpinner from "../../../components/LoadingSpinner";
import { Modal } from "../../../components/Modal";
import { success, error } from '@services/toast-service'
import "./ShipmentRegistration.css"
import moment from "moment";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { VALIDATE_NUMBER } from "@const/constants";
import { useDisplayMode } from "@utils/useDisplayMode";

type Props = {
    dispatch: Dispatcher;
    shipmentState: ShipmentState;
    appState: AppState
};


const ShipmentRegistration: React.FC<Props> = ({ shipmentState, dispatch, appState }) => {
    const { orderDetailUpdate,
        orderDetailInsert,
        selectedPlannedDate,
        order, locationList,
        mixtureList,
        pumpList,
        factoryList,
        selectedPumpId,
        warningTimeSetting
    } = shipmentState;
    const selectedLocationId = appState.currentLocationId;
    const selectedLocation = locationList.find(x => x.id === selectedLocationId);

    const history = useHistory()
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false)
    const { is2D } = useDisplayMode();
    // 2Dモード用: 打設場所ピッカーで「終了分を非表示」を初期状態でチェック
    const [hideFinished, setHideFinished] = useState(true)

    useEffect(() => {
        dispatch(shipmentActions.fetchPumpList({}));
        dispatch(shipmentActions.fetchLocationList()).then((res: any) => {
            // SKY-160: the remembered 打設場所 may have been deleted since (打重ね画像登録 削 removes
            // it for good), e.g. on another browser. Location/GetAll lists every existing
            // 打設場所 (finished ones too), so an id missing from it is stale: forget it and
            // show the screen with no 打設場所 selected, as when none was ever chosen.
            const list = res?.payload;
            if (!Array.isArray(list)) return;
            const rememberedId = selectedLocationId || localStorage.getItem("currentLocationId") || "";
            if (rememberedId && !list.some((l: any) => l.id === rememberedId)) {
                localStorage.removeItem("currentLocationId");
                dispatch(appActions.setCurrentLocationId(""));
                if (!is2D) {
                    history.push("/locationselector");
                }
            }
        });
        dispatch(shipmentActions.fetchFactoryList());
        dispatch(shipmentActions.fetchMixtureList());
    
        if (!selectedLocationId) {

            const storedLocationId = localStorage.getItem("currentLocationId") ?? "";
            if (!storedLocationId) {
                // #8424/BUG-002: in 2D this screen IS the 打設場所 picker. Bouncing to the
                // removed /locationselector creates an infinite redirect loop
                // (/shipment/registration ⇄ /locationselector). Stay here and let the user
                // pick a 打設場所 from the 2D dropdown instead.
                if (!is2D) {
                    history.push("/locationselector")
                }
            } else {
                dispatch(appActions.setCurrentLocationId(storedLocationId));
                dispatch(shipmentActions.fetchWarningTimeSetting(storedLocationId));
                dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: storedLocationId }));
            }

        } else {
            dispatch(shipmentActions.fetchWarningTimeSetting(selectedLocationId));
            dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
        } 
    }, []);

    useEffect(() => {
        if (selectedLocationId && selectedPlannedDate) {
            dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
        }

    }, [selectedLocationId]);

    const isEditableDate = (date: string) => {
        var today = new Date();
        var plannedDate = new Date(date);

        return plannedDate.getDate() >= today.getDate();
    }
    const handleAddOrderDetailClick = async () => {

        const { plannedTransferAmount,
            scheduledShippingStartTime,
            scheduledShippingEndTime,
            factoryId,
            mixtureId,
            loadPerTruck } = orderDetailInsert;

        if (!selectedLocationId) {
            error("打設場所を登録してください。");
            return;
        }

        if (!factoryId) {
            error("出荷工場を選択してください。");
            return;
        }

        if (!mixtureId) {
            error("配合を選択してください。");
            return;
        }

        if (!plannedTransferAmount) {
            error("出荷予定量を入力してください。");
            return;
        }

        if (!scheduledShippingStartTime) {
            error("出荷開始予定時刻を入力してください。");
            return;
        }

        if (!scheduledShippingEndTime) {
            error("出荷終了予定時刻を入力してください。");
            return;
        }

        if (scheduledShippingStartTime >= scheduledShippingEndTime) {
            error("出荷終了予定時刻の入力に誤りがあります。");
            return;
        }

        if (!loadPerTruck) {
            error("車１台の積載量を入力してください。");
            return;
        }
        
        //桁数オーバー
        const valueStr = plannedTransferAmount.toString();

        // 小数点で分割
        const [intPart, decPart = ""] = valueStr.split(".");

        // 整数4桁、少数2桁まで許可
        if (intPart.length > 4 || decPart.length > 2) {
            error("出荷予定量が桁数オーバーです。");
            return;
        }

        try {
            const response = await dispatch(shipmentActions.insertOrderDetail({
                ...orderDetailInsert, locationId: selectedLocationId, plannedDate: selectedPlannedDate, plannedVolume: order.plannedVolume,
                scheduledShippingStartTime: moment(scheduledShippingStartTime).format("YYYY-MM-DDTHH:mm:ss"),
                scheduledShippingEndTime: moment(scheduledShippingEndTime).format("YYYY-MM-DDTHH:mm:ss")
            }));
            if (response.payload && response.payload.success) {
                await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
                success("保存が完了しました。");
                await dispatch(shipmentActions.resetOrderDetailInsert({}));

                await dispatch(shipmentActions.fetchOrderByLocation(selectedLocationId));
                await dispatch(shipmentActions.fetchProcessChartData({ locationId: selectedLocationId }));

            } else {
                error(response.payload.errors[0])
            }

        } catch (error: any) {
            error(error)
        }

    }
    const handleUpdateButtonClick = (id: string) => {
        dispatch(shipmentActions.setUpdatingId(id));
    }

    const handleUpdateCancelButtonClick = (id: string) => {
        dispatch(shipmentActions.setUpdatingId(''));

    }
    const handleDeleteCancelButtonClick = async () => {
        setShowDeleteConfirmModal(false);
        await dispatch(shipmentActions.setDeletingId(''));

    }



    const handleUpdateButtonSaveClick = async (id: string) => {

        const { plannedTransferAmount  } = orderDetailUpdate;

        if (!plannedTransferAmount) {
            error("出荷予定量を入力してください。");
            return;
        }

        //桁数オーバー
        const valueStr = plannedTransferAmount.toString();

        // 小数点で分割
        const [intPart, decPart = ""] = valueStr.split(".");

        // 整数4桁、少数2桁まで許可
        if (intPart.length > 4 || decPart.length > 2) {
            error("出荷予定量が桁数オーバーです。");
            return;
        }
       
        try {
            const response = await dispatch(shipmentActions.updateOrderDetail({ ...orderDetailUpdate, orderid: order.id }));
            if (response.payload && response.payload.success) {
                await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
                await dispatch(shipmentActions.resetUpdatingOrderDetail({}));
                success("保存が完了しました。");

            } else {
                error(response.payload.errors[0])
            }

        } catch (error: any) {
            error(error)
        }
    }

    const handleDeleteButtonClick = async (id: string) => {
        await dispatch(shipmentActions.setDeletingId(id));
        setShowDeleteConfirmModal(true);
    }

    const handleChangeLocationProperty = (key: string, value: string | number) => {
        dispatch(shipmentActions.setLocationData({ key, value, selectedLocationId }));
    }

    const handleChangeWarningTimeSetting = (key: string, value: string | number) => {
        dispatch(shipmentActions.setWarningTimeSetting({ key, value }));
    }

    const handleChangeOrderData = (key: string, value: string | number) => {
        dispatch(shipmentActions.setOrderData({ key, value }));
    }

    const handleSavePlannedVolume = async () => {
        if (!order.plannedVolume || !order.id) {
            error("計画打設量を入力してください。");
            return;
        }

        const response = await dispatch(shipmentActions.updateOrderPlannedVolume({ id: order.id, plannedVolume: order.plannedVolume }));
        if (response.payload && response.payload.success) {
            await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
            success("保存が完了しました。");
        } else {
            error(response.payload.errors[0])
        }

    }



    const handleChangeUpdatingOrderDetailData = (key: string, value: string | number) => {
        dispatch(shipmentActions.setUpdatingOrderDetailData({ key, value }));
    }

    const handleChangeInsertingOrderDetailData = (key: string, value: string | number) => {
        dispatch(shipmentActions.setInsertingOrderDetailData({ key, value }));
    }

    const handleChangePlannedDate = async (value: string) => {
        if (value && value !== '') {
            await dispatch(shipmentActions.setSelectedPlannedDate(moment(value).format("YYYY-MM-DD")));
            // await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
        }
    }

    const handleChangePlannedDateKeyboard = async (value: string) => {
        if (value !== '') {
            await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
        }
    }

    // 2Dモード用: 3Dセレクタを使わずに打設場所を選択する
    const handleChangeSelectedLocation = async (id: string) => {
        if (!id) {
            return;
        }
        await dispatch(appActions.setCurrentLocationId(id));
        localStorage.setItem("currentLocationId", id);
        dispatch(shipmentActions.fetchWarningTimeSetting(id));
        dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: id }));
        dispatch(shipmentActions.fetchOrderByLocation(id));
        // SKY-30: selecting a 打設場所 must NOT navigate away — stay on this screen and keep
        // the selection. The 戻る button is what returns to the 事務所/現場 画面 (with the
        // selected 打設場所 preserved in currentLocationId).
    }

    const handleUpdatePumpClick = async () => {
        if (!selectedLocation || !selectedPumpId) {
            error("無効なデータ");
            return;
        }

        const postedData = { id: selectedLocationId, pumpId: selectedLocation?.pumpId };
        const response = await dispatch(shipmentActions.updateLocationPump(postedData));
        if (response.payload && response.payload.success) {
            success("保存が完了しました。");
        } else {
            error(response.payload.errors[0])
        }
    }


    const handleDeleteOrderDetailConfirmButtonClick = async () => {
        const response = await dispatch(shipmentActions.deleteOrderDetail(shipmentState.deletingOrderDetailId));
        if (response.payload && response.payload.success) {
            await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
            setShowDeleteConfirmModal(false);
            success("保存が完了しました。");
        } else {
            setShowDeleteConfirmModal(false);
            error(response.payload.errors[0])
        }

    }

    const handleUpdateWarningTimeSetting = async () => {

        // SKY-136: 打設場所未選択のまま更新すると BE で空GUIDのJSON変換エラーになる。
        // 事前にチェックして日本語のエラーメッセージを表示する。
        if (!selectedLocationId) {
            error("打設場所を選択してください。");
            return;
        }

        const { beginDeliveryLimit,
            beginDeliveryWarningTime,
            endDeliveryWarningTime,
            overlapLimitTime,
            overlapLimitWarningTime
        } = warningTimeSetting;

        if (!beginDeliveryLimit || (beginDeliveryLimit && !VALIDATE_NUMBER.test(beginDeliveryLimit.toString()))) {
            error("打設制限時間は数値で入力してください。")
            return;
        }
        if (!beginDeliveryWarningTime || (beginDeliveryWarningTime && !VALIDATE_NUMBER.test(beginDeliveryWarningTime.toString()))) {
            error("荷卸開始警告時間は数値で入力してください。")
            return;
        }
        if (!endDeliveryWarningTime || (endDeliveryWarningTime && !VALIDATE_NUMBER.test(endDeliveryWarningTime.toString()))) {
            error("荷卸終了警告時間は数値で入力してください。")
            return;
        }
        if (!overlapLimitTime || (overlapLimitTime && !VALIDATE_NUMBER.test(overlapLimitTime.toString()))) {
            error("打重ね制限時間は数値で入力してください。")
            return;
        }
        if (!overlapLimitWarningTime || (overlapLimitWarningTime && !VALIDATE_NUMBER.test(overlapLimitWarningTime.toString()))) {
            error("打重ね警告時間は数値で入力してください。")
            return;
        }

        const response = await dispatch(shipmentActions.updateLocationLimitTime({
            id: selectedLocationId,
            unloadTimeLimit: warningTimeSetting?.beginDeliveryLimit,
            beginUnloadWarningTime: warningTimeSetting?.beginDeliveryWarningTime,
            endDeliveryLimit: warningTimeSetting?.beginDeliveryLimit,
            endUnloadWarningTime: warningTimeSetting?.endDeliveryWarningTime,
            overlapLimitTime: warningTimeSetting?.overlapLimitTime,
            overlapLimitWarningTime: warningTimeSetting?.overlapLimitWarningTime
        }));
        if (response.payload && response.payload.success) {
            await dispatch(shipmentActions.fetchOrder({ plannedDate: selectedPlannedDate, locationId: selectedLocationId }));
            success("保存が完了しました。");
        } else {
            error(response.payload.errors[0])
        }

    }
    const isEditable = order.id && selectedPlannedDate && isEditableDate(selectedPlannedDate);

    // 2Dモード用: 「終了分を非表示」がチェックされている場合は終了した打設場所を除外する。
    // SKY-132: 打設場所の終了判定は status === "FinishPumping"（Location/GetAll の実データ）。
    // 以前の isFinished / status === "finished" は実データに存在せず、フィルタが効かなかった。
    const isLocationFinished = (loc: any) => loc.status === "FinishPumping";
    // 出荷登録＿詳細① ②: display 「全体モデル名/ブロック名/リフト名」. The BE `name` is already
    // "モデル名 ブロック名 リフト名" (space-separated), so strip that suffix to recover the
    // model name instead of appending blockName/liftName again (which duplicated them).
    const locationDisplayName = (loc: any) => {
        const name: string = loc.name ?? "";
        const suffix = ` ${loc.blockName ?? ""} ${loc.liftName ?? ""}`;
        const modelName = name.endsWith(suffix) ? name.slice(0, name.length - suffix.length) : name;
        return [modelName, loc.blockName, loc.liftName].filter(Boolean).join("/");
    };
    // 出荷登録＿詳細① ②: 「降順」で表示する
    const locationOptions = locationList
        .filter(loc => !hideFinished || !isLocationFinished(loc))
        .slice()
        .sort((a, b) => locationDisplayName(b).localeCompare(locationDisplayName(a), "ja"));

    const renderList = order.details && order.details.length ? (
        order.details.map((orderDetail: TShipmentManagement.OrderDetail) => (
            <tr key={orderDetail.id}>
                <td>
                    {
                        orderDetail.id !== shipmentState.updatingOrderDetailId ?
                            (
                                <div className="wd-100">
                                    <input type="button" name="btnRowEdit" value="編" onClick={() => { handleUpdateButtonClick(orderDetail.id) }} hidden={process.env.REACT_APP_MODE_RUN === 'viewer'} />
                                    <input type="button" name="btnRowDelete" value="削" onClick={() => { handleDeleteButtonClick(orderDetail.id) }} className="ml-3" hidden={process.env.REACT_APP_MODE_RUN === 'viewer'} />
                                </div>
                            )
                            :
                            (
                                <div className="wd-100">
                                    <input type="button" name="btnRowEdit" value="更新" onClick={() => handleUpdateButtonSaveClick(orderDetail.id)} />
                                    <input type="button" name="btnRowDelete" value="キャンセル" onClick={() => handleUpdateCancelButtonClick(orderDetail.id)} className="ml-3" />
                                </div>
                            )
                    }
                </td>
                <td>
                    {
                        orderDetail.factoryName
                    }
                </td>
                <td>
                    {
                        orderDetail.mixtureName
                    }
                </td>
                <td>
                    {
                        orderDetail.id !== shipmentState.updatingOrderDetailId ? orderDetail.plannedTransferAmount.toFixed(2) :
                            (<input type="number" required className="form-input" value={orderDetailUpdate.plannedTransferAmount} onChange={(e) => { handleChangeUpdatingOrderDetailData('plannedTransferAmount', e.target.value) }} />)

                    }
                </td>
                <td>
                    {  
                        moment(orderDetail.scheduledShippingStartTime).format("YYYY/MM/DD HH:mm")
                    }
                </td>
                <td>
                    {
                        moment(orderDetail.scheduledShippingEndTime).format("YYYY/MM/DD HH:mm")
                    }
                </td>
                <td>
                    {/*{*/}
                    {/*    orderDetail.id !== shipmentState.updatingOrderDetailId ? orderDetail.transportTime.toFixed(2) :*/}
                    {/*        (*/}
                    {/*            <input type="number" required className="form-input" value={orderDetailUpdate.transportTime || 0} onChange={(e) => { handleChangeUpdatingOrderDetailData('transportTime', parseFloat(e.target.value)) }} />*/}
                    {/*        )*/}
                    {/*}*/}
                    {orderDetail.transportTime}
                </td>

                <td>
                    {
                        orderDetail.loadPerTruck
                    }
                </td>
                <td>{orderDetail.transferAmount}</td>
            </tr>
        ))
    ) : (
        <></>
    );

    return (
        <>
            <LoadingSpinner isShowing={shipmentState.isLoading} />
            <div className="settingItemPageTitle">
                <p>出荷登録</p>
            </div>
            <div className="row filter-container">
                <div className="col-6">
                    {is2D && (
                        <div className="form-group casting-location-filter mb-3">
                            <label className="control-label">打設場所</label>
                            {/* SKY-132: 「終了分を非表示」を打設場所ドロップダウンの右側に配置し、
                                打設場所を選択しても隠れないようにする（横並び）。 */}
                            <div className="d-flex align-items-center">
                                <select
                                    className="form-select"
                                    style={{ width: 300 }}
                                    value={selectedLocationId ?? ""}
                                    onChange={e => handleChangeSelectedLocation(e.currentTarget.value)}
                                >
                                    <option value=""></option>
                                    {locationOptions.map(loc => (
                                        <option key={loc.id} value={loc.id}>{locationDisplayName(loc)}</option>
                                    ))}
                                </select>
                                <div className="form-check ml-3">
                                    <input
                                        id="chkHideFinished"
                                        type="checkbox"
                                        className="form-check-input"
                                        checked={hideFinished}
                                        onChange={e => setHideFinished(e.target.checked)}
                                    />
                                    <label className="form-check-label ml-1" htmlFor="chkHideFinished">終了分を非表示</label>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className="form-group date-filter">
                        <label className="control-label">出荷予定日</label>
                        <input
                            className="form-control"
                            value={selectedPlannedDate}
                            type="date"
                            style={{ width: 300 }}
                            onChange={e => handleChangePlannedDate(e.target.value)}
                            onBlur={event => handleChangePlannedDateKeyboard(event.target.value)}
                        />

                    </div>
                    <div className="form-group mt-3">
                        <label className="control-label">計画打設量（m3）</label>
                        <div>
                            <input
                                className="form-control d-inline"
                                value={order.plannedVolume}
                                type="number"
                                min={0}
                                style={{ width: 100 }}
                                onChange={e => handleChangeOrderData("plannedVolume", parseFloat(e.target.value))}
                            />
                            <button hidden={!isEditable || process.env.REACT_APP_MODE_RUN === 'viewer'} className="btn btn-primary bg-grey mb-1 ml-3" onClick={() => handleSavePlannedVolume()} >計画打設量変更</button>
                        </div>

                    </div>
                </div>

                <div className="col-6">
                    <table className="table table-bordered timeWarningTable">
                        <tbody>
                            <tr>
                                <td style={{ backgroundColor: '#FF0000' }}>
                                    打設制限時間（分）：
                                </td>
                                <td style={{ backgroundColor: '#FF0000' }}>
                                    <input
                                        className="form-control d-inline"
                                        value={warningTimeSetting.beginDeliveryLimit}
                                        type="text"
                                        style={{ width: 100 }}
                                        onChange={e => { handleChangeWarningTimeSetting("beginDeliveryLimit", e.target.value) }}
                                    />
                                </td>

                            </tr>
                            <tr>
                                <td style={{ backgroundColor: '#FF66FF' }}>
                                    荷卸開始警告時間（分）：
                                </td>
                                <td style={{ backgroundColor: '#FF66FF' }}>
                                    <input
                                        className="form-control d-inline"
                                        value={warningTimeSetting.beginDeliveryWarningTime}
                                        type="text"
                                        style={{ width: 100 }}
                                        onChange={e => { handleChangeWarningTimeSetting("beginDeliveryWarningTime", e.target.value) }}
                                    />
                                </td>

                            </tr>
                            <tr>
                                <td style={{ backgroundColor: '#FF66FF' }}>
                                    荷卸終了警告時間（分）：
                                </td>
                                <td style={{ backgroundColor: '#FF66FF' }}>
                                    <input
                                        className="form-control d-inline"
                                        value={warningTimeSetting.endDeliveryWarningTime}
                                        type="text"
                                        style={{ width: 100 }}
                                        onChange={e => { handleChangeWarningTimeSetting("endDeliveryWarningTime", e.target.value) }}
                                    />
                                </td>

                            </tr>
                            <tr>
                                <td colSpan={2} />
                            </tr>
                            <tr>
                                <td style={{ backgroundColor: '#FF0000' }}>
                                    打重ね制限時間（分）：
                                </td>
                                <td style={{ backgroundColor: '#FF0000' }}>
                                    <input
                                        className="form-control d-inline"
                                        value={warningTimeSetting.overlapLimitTime}
                                        type="text"
                                        style={{ width: 100 }}
                                        onChange={e => { handleChangeWarningTimeSetting("overlapLimitTime", e.target.value) }}
                                    />
                                </td>

                            </tr>

                            <tr>
                                <td style={{ backgroundColor: '#FF66FF' }}>
                                    打重ね警告時間（分）：
                                </td>
                                <td style={{ backgroundColor: '#FF66FF' }}>
                                    <input
                                        className="form-control d-inline"
                                        value={warningTimeSetting.overlapLimitWarningTime}
                                        type="text"
                                        style={{ width: 100 }}
                                        onChange={e => { handleChangeWarningTimeSetting("overlapLimitWarningTime", e.target.value) }}
                                    />
                                </td>

                            </tr>
                        </tbody>

                    </table>
                    <button onClick={() => handleUpdateWarningTimeSetting()} className="btn btn-primary bg-grey mb-1 ml-3" hidden={process.env.REACT_APP_MODE_RUN === 'viewer'}>警告時間更新</button>
                </div>

            </div>
            <div className="content shipment-container">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th />
                            <th scope="col" style={{ width: "15%" }}>出荷工場</th>
                            <th scope="col" style={{ width: "15%" }}>配合</th>
                            <th scope="col">出荷予定量（m3）</th>
                            <th scope="col">出荷開始予定時刻</th>
                            <th scope="col">出荷終了予定時刻</th>
                            <th scope="col">出荷間隔（分）</th>
                            <th scope="col">車1台の積載量（m3）</th>
                            <th scope="col">出荷済み量（m3）</th>
                        </tr>
                    </thead>
                    <tbody>
                        {renderList}
                        {(!order.details || order.details.length === 0) &&
                            (
                                <tr>
                                    <td />
                                    <td />
                                    <td />
                                    <td>
                                        0
                                    </td>
                                    <td>
                                        0
                                    </td>
                                    <td>
                                        0
                                    </td>
                                    <td>
                                        0
                                    </td>
                                    <td>
                                        0
                                    </td>
                                    <td>
                                        0
                                    </td>
                                </tr>
                            )}
                        {process.env.REACT_APP_MODE_RUN !== 'viewer' && (
                        <tr className="insertform">
                            <td>
                                <input type="button" name="btnAdd" value="追加" onClick={handleAddOrderDetailClick} />
                            </td>
                            <td>
                                <select className="form-select wd-150" defaultValue={orderDetailInsert.factoryId} onBlur={e => handleChangeInsertingOrderDetailData('factoryId', e.currentTarget.value)}>
                                    <option value=""></option>
                                    {factoryList?.length && factoryList?.length && factoryList.map(f => (
                                        <option key={f.id} value={f.id} selected={f.id === orderDetailInsert.factoryId}>{f.name}</option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <select className="form-select wd-150" defaultValue={orderDetailInsert.mixtureId} onBlur={e => handleChangeInsertingOrderDetailData('mixtureId', e.currentTarget.value)}>
                                    <option value=""></option>
                                    {mixtureList?.length && mixtureList.map(m => (
                                        <option key={m.id} value={m.id} selected={m.id === orderDetailInsert.mixtureId}>{m.name1 ?? ""} {m.name2 ?? ""}</option>
                                    ))}
                                </select>
                            </td>
                            <td>
                                <input type="number" required className="form-control mw-135" value={orderDetailInsert.plannedTransferAmount} onChange={(e) => { handleChangeInsertingOrderDetailData('plannedTransferAmount', e.target.value) }} />
                            </td>
                            <td className="wd-150">

                                <DatePicker selected={orderDetailInsert?.scheduledShippingStartTime !== '' ? new Date(moment(orderDetailInsert.scheduledShippingStartTime).format()) : null}
                                    className="form-control  wd-210"
                                    showTimeSelect
                                    dateFormat="yyyy/MM/dd HH:mm"
                                    onChange={(date: Date) => {
                                        if (date) {
                                            handleChangeInsertingOrderDetailData('scheduledShippingStartTime', date.toISOString());
                                        }
                                    }} />
                            </td>
                            <td>

                                <DatePicker selected={orderDetailInsert?.scheduledShippingEndTime !== '' ? new Date(moment(orderDetailInsert.scheduledShippingEndTime).format()) : null}
                                    className="form-control  wd-210"
                                    showTimeSelect
                                    dateFormat="yyyy/MM/dd HH:mm"
                                    onChange={(date: Date) => {
                                        if (date) {
                                            handleChangeInsertingOrderDetailData('scheduledShippingEndTime', date.toISOString())
                                        }
                                    }} />
                            </td>
                            <td>
                                <p className="wd-150" />
                            </td>
                            <td>
                                <input type="number" required className="form-control mw-135" value={orderDetailInsert.loadPerTruck} onChange={(e) => { handleChangeInsertingOrderDetailData('loadPerTruck', e.target.value) }} />
                            </td>

                            <td>
                                <p className="wd-150" />
                            </td>
                        </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="goHomeButtonContainer">
                {/* SKY-30: 戻る returns to the 事務所/現場 画面 keeping the selected 打設場所. */}
                <input type="button" value="戻る" onClick={() => {
                    const role = localStorage.getItem("role");
                    history.push(role === "WorkingSite" ? "/construction" : "/location");
                }} className="goHomeButton" />
            </div>
            <Modal isActive={showDeleteConfirmModal} onClose={() => handleDeleteCancelButtonClick()} width="30%">
                <div className="modal-body">
                    <p>削除します。よろしいですか？</p>
                </div>
                <div className="modal-footer">
                    <input type="button" onClick={handleDeleteOrderDetailConfirmButtonClick} value="削除" />
                    <input type="button" className="ml-3" onClick={() => { handleDeleteCancelButtonClick() }} value="キャンセル" />
                </div>
            </Modal>
        </>
    );
};

export default connect((state: RootState) => ({
    shipmentState: state.shipment,
    appState: state.app
}))(ShipmentRegistration);
